// Header Component - AgentBheem ERP
// Vue-inspired Modern Dark Theme with Indigo/Purple Accent
import { useState, useRef, useEffect, useMemo } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useMenuStore } from '@/stores/menuStore'
import { useAuthStore } from '@/features/auth/store/authStore'
import { useUnreadNotifications } from '@/hooks/useNotifications'
import { NotificationsPanel } from './NotificationsPanel'
import { SettingsPanel } from './SettingsPanel'
import { ProfilePanel } from './ProfilePanel'
import {
  Menu,
  Search,
  Bell,
  Settings,
  LogOut,
  User,
  ChevronDown,
  X,
  Video,
  Mail,
  MessageSquare,
  Calendar,
  FileText,
  Cloud,
  Grid3X3,
  LayoutDashboard,
  FolderKanban,
  ListTodo,
  Users,
  Clock,
  Table,
  Presentation,
  StickyNote,
  ClipboardList,
  Globe,
  PlayCircle,
  LayoutGrid,
  Briefcase,
  BarChart3,
  CheckSquare,
  UserCheck,
  FolderOpen,
  Zap,
  Flag,
  GanttChart,
  AlarmClock,
  Workflow,
  AlertTriangle,
  CalendarDays,
  Building2,
  Rocket,
  Newspaper,
} from 'lucide-react'

// Format role for display (e.g., "super_admin" → "Super Admin")
function formatRole(role: string | undefined): string {
  if (!role) return 'User'
  return role
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

// Workspace Apps - shown in header bar
const workspaceApps = [
  { name: 'Meet', icon: Video, path: '/bheem-meet', gradient: 'from-teal-600 to-teal-700' },
  { name: 'Mail', icon: Mail, path: '/bheem-mail', gradient: 'from-red-500 to-red-700' },
  { name: 'Chat', icon: MessageSquare, path: '/dashboard/bheem-chat', gradient: 'from-purple-600 to-purple-800' },
  { name: 'Feed', icon: Newspaper, path: '/dashboard/feed', gradient: 'from-orange-500 to-orange-700' },
  { name: 'Calendar', icon: Calendar, path: '/bheem-calender', gradient: 'from-blue-500 to-blue-700' },
  { name: 'Docs', icon: FileText, path: '/bheem-docs', gradient: 'from-blue-500 to-blue-600' },
  { name: 'Drive', icon: Cloud, path: '/bheem-drive', gradient: 'from-amber-500 to-orange-600' },
]

// All Bheem Apps - shown in dropdown
const allBheemApps = [
  { name: 'Meet', icon: Video, path: '/bheem-meet', gradient: 'from-teal-600 to-teal-700' },
  { name: 'Mail', icon: Mail, path: '/bheem-mail', gradient: 'from-red-500 to-red-700' },
  { name: 'Chat', icon: MessageSquare, path: '/dashboard/bheem-chat', gradient: 'from-purple-600 to-purple-800' },
  { name: 'Feed', icon: Newspaper, path: '/dashboard/feed', gradient: 'from-orange-500 to-orange-700' },
  { name: 'Calendar', icon: Calendar, path: '/bheem-calender', gradient: 'from-blue-500 to-blue-700' },
  { name: 'Docs', icon: FileText, path: '/bheem-docs', gradient: 'from-blue-500 to-blue-600' },
  { name: 'Drive', icon: Cloud, path: '/bheem-drive', gradient: 'from-amber-500 to-orange-600' },
  { name: 'Sheets', icon: Table, path: '/bheem-sheets', gradient: 'from-green-500 to-green-700' },
  { name: 'Slides', icon: Presentation, path: '/bheem-slides', gradient: 'from-yellow-500 to-yellow-600' },
  { name: 'Notes', icon: StickyNote, path: '/bheem-notes', gradient: 'from-yellow-400 to-amber-500' },
  { name: 'Forms', icon: ClipboardList, path: '/bheem-forms', gradient: 'from-violet-500 to-violet-700' },
  { name: 'Sites', icon: Globe, path: '/bheem-sites', gradient: 'from-indigo-500 to-indigo-700' },
  { name: 'Videos', icon: PlayCircle, path: '/bheem-videos', gradient: 'from-red-600 to-red-800' },
  { name: 'Workspace', icon: LayoutGrid, path: '/bheem-workspace', gradient: 'from-cyan-500 to-cyan-700' },
]

// PM Menu Item type with admin-only flag
interface PMMenuItem {
  name: string
  icon: typeof LayoutDashboard
  path: string
  adminOnly?: boolean
}

interface PMMenuSection {
  title: string
  items: PMMenuItem[]
  adminOnly?: boolean
}

// PM Menu sections - Full navigation for PM module
// Items/sections with adminOnly: true are hidden from employees
const pmMenuSections: PMMenuSection[] = [
  {
    title: 'OVERVIEW',
    items: [
      { name: 'Dashboard', icon: LayoutDashboard, path: '/dashboard/pm' },
      { name: 'Project & Task Manager', icon: FolderKanban, path: '/dashboard/pm/manager', adminOnly: true },
      { name: 'Analytics', icon: BarChart3, path: '/dashboard/pm/analytics', adminOnly: true },
    ],
  },
  {
    title: 'PROJECTS',
    items: [
      { name: 'All Projects', icon: Briefcase, path: '/dashboard/pm/projects', adminOnly: true },
      { name: 'My Projects', icon: FolderOpen, path: '/dashboard/pm/my-projects' },
    ],
  },
  {
    title: 'TASKS',
    items: [
      { name: 'All Tasks', icon: ListTodo, path: '/dashboard/pm/tasks', adminOnly: true },
      { name: 'My Tasks', icon: CheckSquare, path: '/dashboard/pm/my-tasks' },
      { name: 'Assigned by Me', icon: UserCheck, path: '/dashboard/pm/assigned-by-me' },
    ],
  },
  {
    title: 'AGILE / SCRUM',
    items: [
      { name: 'Sprints', icon: Zap, path: '/dashboard/pm/sprints' },
      { name: 'Backlog', icon: ClipboardList, path: '/dashboard/pm/backlog', adminOnly: true },
    ],
  },
  {
    title: 'PLANNING',
    adminOnly: true, // Entire section hidden for employees
    items: [
      { name: 'Milestones', icon: Flag, path: '/dashboard/pm/milestones' },
      { name: 'Gantt Chart', icon: GanttChart, path: '/dashboard/pm/gantt' },
      { name: 'Timeline', icon: Clock, path: '/dashboard/pm/timeline' },
      { name: 'Deadlines', icon: AlarmClock, path: '/dashboard/pm/deadlines' },
    ],
  },
  {
    title: 'CALENDARS',
    items: [
      { name: 'Calendar', icon: CalendarDays, path: '/dashboard/pm/calendar' },
    ],
  },
  {
    title: 'COMMUNICATION',
    items: [
      { name: 'Chat', icon: MessageSquare, path: '/dashboard/pm/chat' },
      { name: 'Feed', icon: Newspaper, path: '/dashboard/pm/feed' },
    ],
  },
  {
    title: 'OPERATIONS',
    items: [
      { name: 'Time Tracking', icon: Clock, path: '/dashboard/pm/time-tracking' },
      { name: 'Resources', icon: Users, path: '/dashboard/pm/resources', adminOnly: true },
      { name: 'Risks', icon: AlertTriangle, path: '/dashboard/pm/risks', adminOnly: true },
    ],
  },
  {
    title: 'BUSINESS',
    adminOnly: true, // Entire section hidden for employees
    items: [
      { name: 'Clients', icon: Building2, path: '/dashboard/pm/clients' },
      { name: 'Workflows', icon: Workflow, path: '/dashboard/pm/workflows' },
    ],
  },
]

export function Header() {
  const navigate = useNavigate()
  const { isMobileOpen, setMobileOpen } = useMenuStore()
  const { user, logout } = useAuthStore()
  const [isUserMenuOpen, setUserMenuOpen] = useState(false)
  const [isNotificationsOpen, setNotificationsOpen] = useState(false)
  const [isSettingsOpen, setSettingsOpen] = useState(false)
  const [isProfileOpen, setProfileOpen] = useState(false)
  const [showAllApps, setShowAllApps] = useState(false)
  const [pmOpen, setPmOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const userMenuRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const allAppsRef = useRef<HTMLDivElement>(null)
  const pmRef = useRef<HTMLDivElement>(null)

  // Mock PM stats (replace with real data)
  const pmStats = { projects: 12, tasks: 47, overdue: 3 }

  // Fetch unread notifications count
  const { data: notificationsData } = useUnreadNotifications()
  const unreadCount = notificationsData?.items?.length || 0

  // Check if user is admin/manager for PM menu filtering
  const isAdminOrManager = useMemo(() => {
    if (!user) return false
    const role = (user.role || '').toLowerCase()
    return [
      'superadmin',
      'super_admin',
      'admin',
      'manager',
      'project_manager',
      'projectmanager',  // Without underscore variant
      'team_lead',
      'teamlead',        // Without underscore variant
      'pm_admin',
    ].includes(role)
  }, [user])

  // Filter PM menu sections based on role
  const filteredPmMenuSections = useMemo(() => {
    if (isAdminOrManager) return pmMenuSections

    // Filter for employees: hide adminOnly sections and items
    return pmMenuSections
      .filter(section => !section.adminOnly) // Remove admin-only sections
      .map(section => ({
        ...section,
        items: section.items.filter(item => !item.adminOnly) // Remove admin-only items
      }))
      .filter(section => section.items.length > 0) // Remove empty sections
  }, [isAdminOrManager])

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false)
      }
      if (allAppsRef.current && !allAppsRef.current.contains(event.target as Node)) {
        setShowAllApps(false)
      }
      if (pmRef.current && !pmRef.current.contains(event.target as Node)) {
        setPmOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Keyboard shortcut for search
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault()
        setSearchOpen(true)
        setTimeout(() => searchRef.current?.focus(), 100)
      }
      if (event.key === 'Escape') {
        setSearchOpen(false)
        setSearchQuery('')
        setShowAllApps(false)
        setPmOpen(false)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  const handleLogout = () => {
    logout()
    navigate('/auth/login')
  }

  // Close other panels when opening one
  const openNotifications = () => {
    setNotificationsOpen(true)
    setSettingsOpen(false)
    setUserMenuOpen(false)
    setProfileOpen(false)
  }

  const openSettings = () => {
    setSettingsOpen(true)
    setNotificationsOpen(false)
    setUserMenuOpen(false)
    setProfileOpen(false)
  }

  const openProfile = () => {
    setProfileOpen(true)
    setNotificationsOpen(false)
    setSettingsOpen(false)
    setUserMenuOpen(false)
  }

  return (
    <header
      className={cn(
        'sticky top-0 z-30 h-[70px]',
        'bg-[#0f172a]/95 backdrop-blur-xl',
        'border-b border-white/[0.08]',
        'transition-all duration-300'
      )}
    >
      <div className="flex h-full items-center justify-between px-4 lg:px-6">
        {/* Left Section - Logo & Brand */}
        <div className="flex items-center gap-4">
          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileOpen(!isMobileOpen)}
            className={cn(
              'lg:hidden p-2.5 rounded-lg transition-all duration-200',
              'text-slate-400',
              'hover:text-slate-200',
              'hover:bg-indigo-500/10',
              'active:scale-95'
            )}
          >
            <Menu className="h-5 w-5" />
          </button>

          {/* Logo & Brand - Hidden on mobile, visible on larger screens */}
          <div className="hidden lg:flex items-center gap-3">
            <div
              className={cn(
                'flex items-center justify-center rounded-xl p-1.5',
                'bg-gradient-to-br from-indigo-500 to-purple-600',
                'shadow-lg shadow-indigo-500/40',
                'h-10 w-10'
              )}
            >
              <img
                src="/logo.png"
                alt="AgentBheem"
                className="h-full w-full object-contain"
              />
            </div>
            <div className="flex flex-col">
              <span className="text-lg font-bold bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">
                Agent Bheem
              </span>
              <span className="text-[9px] text-slate-500 uppercase tracking-widest font-medium">
                AI Assistant for Business Automation!
              </span>
            </div>
          </div>
        </div>

        {/* Center Section - Workspace Apps Bar */}
        <div className="hidden lg:flex items-center gap-1">
          {workspaceApps.map((app) => (
            <Link
              key={app.name}
              to={app.path}
              className="group flex flex-col items-center gap-1 px-3 py-1.5 rounded-xl hover:bg-white/[0.05] transition-all duration-200"
              title={app.name}
            >
              <div
                className={cn(
                  'flex items-center justify-center w-10 h-10 rounded-xl',
                  'bg-gradient-to-br',
                  app.gradient,
                  'shadow-lg group-hover:shadow-xl group-hover:scale-105 transition-all duration-200'
                )}
              >
                <app.icon className="h-5 w-5 text-white" />
              </div>
              <span className="text-[10px] text-slate-400 group-hover:text-slate-200 font-medium transition-colors">
                {app.name}
              </span>
            </Link>
          ))}

          {/* All Apps Button */}
          <div className="relative" ref={allAppsRef}>
            <button
              onClick={() => setShowAllApps(!showAllApps)}
              className={cn(
                'group flex flex-col items-center gap-1 px-3 py-1.5 rounded-xl transition-all duration-200',
                showAllApps ? 'bg-indigo-500/20' : 'hover:bg-white/[0.05]'
              )}
            >
              <div
                className={cn(
                  'flex items-center justify-center w-10 h-10 rounded-xl',
                  'bg-gradient-to-br from-slate-600 to-slate-700',
                  'shadow-lg group-hover:shadow-xl group-hover:scale-105 transition-all duration-200'
                )}
              >
                <Grid3X3 className="h-5 w-5 text-white" />
              </div>
              <span className="text-[10px] text-slate-400 group-hover:text-slate-200 font-medium transition-colors">
                All
              </span>
            </button>

            {/* All Apps Dropdown */}
            {showAllApps && (
              <div
                className={cn(
                  'absolute right-0 top-full mt-2 w-[420px]',
                  'bg-[#1e293b]/95 backdrop-blur-xl',
                  'border border-white/[0.08] rounded-xl',
                  'shadow-2xl shadow-black/40',
                  'p-4 z-50',
                  'animate-scale-up'
                )}
              >
                <p className="text-xs font-semibold text-slate-400 mb-3 px-1">All Bheem Apps</p>
                <div className="grid grid-cols-4 gap-3 max-h-[400px] overflow-y-auto">
                  {allBheemApps.map((app) => (
                    <Link
                      key={app.name}
                      to={app.path}
                      onClick={() => setShowAllApps(false)}
                      className="group flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-white/[0.05] transition-all duration-200"
                    >
                      <div
                        className={cn(
                          'flex items-center justify-center w-11 h-11 rounded-xl',
                          'bg-gradient-to-br',
                          app.gradient,
                          'shadow-lg group-hover:shadow-xl group-hover:scale-105 transition-all duration-200'
                        )}
                      >
                        <app.icon className="h-5 w-5 text-white" />
                      </div>
                      <span className="text-[10px] text-slate-400 group-hover:text-slate-200 font-medium transition-colors text-center">
                        {app.name}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-2">
          {/* PM Button */}
          <div className="relative hidden lg:block" ref={pmRef}>
            <button
              onClick={() => setPmOpen(!pmOpen)}
              className={cn(
                'relative flex items-center gap-1.5 px-3 py-2 rounded-xl',
                'bg-gradient-to-r from-indigo-500/20 to-purple-500/20',
                'border border-indigo-500/30',
                'text-indigo-400 font-bold text-sm',
                'hover:from-indigo-500/30 hover:to-purple-500/30',
                'hover:border-indigo-500/40',
                'transition-all duration-200',
                pmOpen && 'from-indigo-500/30 to-purple-500/30 border-indigo-500/40'
              )}
            >
              <Rocket className="h-4 w-4" />
              <span>PM</span>
              {pmStats.overdue > 0 && (
                <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[20px] h-[20px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full shadow-lg shadow-red-500/30">
                  {pmStats.overdue}
                </span>
              )}
            </button>

            {/* PM Dropdown Panel */}
            {pmOpen && (
              <div
                className={cn(
                  'absolute right-0 top-full mt-2 w-[260px]',
                  'bg-white rounded-2xl',
                  'shadow-2xl shadow-black/25',
                  'border border-gray-100',
                  'z-50 overflow-hidden',
                  'animate-scale-up'
                )}
              >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600">
                  <div className="flex items-center gap-2.5">
                    <Rocket className="h-5 w-5 text-white" />
                    <span className="text-white font-bold text-[15px]">Project Management</span>
                  </div>
                  <button
                    onClick={() => setPmOpen(false)}
                    className="p-1.5 rounded-lg text-white/80 hover:text-white hover:bg-white/20 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                {/* Stats */}
                <div className="flex items-center py-4 border-b border-gray-100">
                  <div className="text-center flex-1">
                    <p className="text-2xl font-bold text-slate-800">{pmStats.projects}</p>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide font-semibold mt-0.5">Projects</p>
                  </div>
                  <div className="text-center flex-1 border-x border-gray-200">
                    <p className="text-2xl font-bold text-slate-800">{pmStats.tasks}</p>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide font-semibold mt-0.5">Tasks</p>
                  </div>
                  <div className="text-center flex-1">
                    <p className="text-2xl font-bold text-red-500">{pmStats.overdue}</p>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide font-semibold mt-0.5">Overdue</p>
                  </div>
                </div>

                {/* Menu Sections - Scrollable (filtered by role) */}
                <div className="max-h-[600px] overflow-y-auto py-2">
                  {filteredPmMenuSections.map((section) => (
                    <div key={section.title} className="mb-1">
                      <p className="px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                        {section.title}
                      </p>
                      {section.items.map((item) => (
                        <Link
                          key={item.name}
                          to={item.path}
                          onClick={() => setPmOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
                        >
                          <item.icon className="h-[18px] w-[18px] text-slate-400" />
                          <span className="text-[14px] font-medium">{item.name}</span>
                        </Link>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Search Button */}
          <button
            onClick={() => setSearchOpen(!searchOpen)}
            className={cn(
              'p-2.5 rounded-xl transition-all duration-200',
              'bg-slate-800/50 border border-white/[0.08]',
              searchOpen
                ? 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30'
                : 'text-slate-400 hover:text-slate-200 hover:bg-indigo-500/10'
            )}
          >
            <Search className="h-5 w-5" />
          </button>

          {/* Search Overlay */}
          {searchOpen && (
            <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 bg-black/50 backdrop-blur-sm">
              <div
                className={cn(
                  'w-full max-w-xl mx-4',
                  'bg-[#1e293b]/95 backdrop-blur-xl',
                  'border border-white/[0.08] rounded-2xl',
                  'shadow-2xl shadow-black/40',
                  'animate-scale-up'
                )}
              >
                <div className="flex items-center gap-3 px-4 py-4 border-b border-white/[0.08]">
                  <Search className="h-5 w-5 text-indigo-400" />
                  <input
                    ref={searchRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search anything..."
                    className="flex-1 bg-transparent text-lg text-slate-200 placeholder-slate-500 outline-none"
                    autoFocus
                  />
                  <button
                    onClick={() => {
                      setSearchOpen(false)
                      setSearchQuery('')
                    }}
                    className="p-2 rounded-lg hover:bg-slate-700/50 text-slate-400 hover:text-slate-200 transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="p-4 text-center text-slate-500 text-sm">
                  <p>Press <kbd className="px-2 py-1 bg-slate-700/50 rounded text-xs">ESC</kbd> to close</p>
                </div>
              </div>
            </div>
          )}

          {/* Notifications */}
          <div className="relative">
            <button
              onClick={openNotifications}
              className={cn(
                'relative p-2.5 rounded-xl transition-all duration-200',
                'bg-slate-800/50 border border-white/[0.08]',
                isNotificationsOpen
                  ? 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-indigo-500/10'
              )}
            >
              <Bell className="h-5 w-5" />
              {/* Notification Badge */}
              {unreadCount > 0 && (
                <span
                  className={cn(
                    'absolute -top-1 -right-1',
                    'min-w-[20px] h-[20px] flex items-center justify-center px-1.5',
                    'bg-gradient-to-r from-red-500 to-orange-500',
                    'text-white text-[10px] font-bold rounded-full',
                    'shadow-lg shadow-red-500/30',
                    'animate-pulse'
                  )}
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
            <NotificationsPanel
              isOpen={isNotificationsOpen}
              onClose={() => setNotificationsOpen(false)}
            />
          </div>

          {/* User Menu */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setUserMenuOpen(!isUserMenuOpen)}
              className={cn(
                'flex items-center gap-2 rounded-xl px-2 py-1.5',
                'bg-slate-800/50 border border-white/[0.08]',
                'transition-all duration-200',
                'hover:bg-indigo-500/10 hover:border-indigo-500/20',
                isUserMenuOpen && 'bg-indigo-500/10 border-indigo-500/20'
              )}
            >
              {/* Avatar */}
              <div className="relative">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center shadow-lg">
                  <span className="text-sm font-bold text-white">
                    {user?.full_name?.charAt(0)?.toUpperCase() ||
                      user?.username?.charAt(0)?.toUpperCase() ||
                      'U'}
                  </span>
                </div>
                {/* Online indicator */}
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-[#0f172a]" />
              </div>

              {/* Name & Role */}
              <div className="hidden md:block text-left">
                <p className="text-sm font-semibold text-slate-200 truncate max-w-[100px]">
                  {user?.full_name || user?.username || 'User'}
                </p>
                <p className="text-[10px] text-slate-500 font-medium uppercase truncate max-w-[100px]">
                  {formatRole(user?.role)}
                </p>
              </div>

              <ChevronDown
                className={cn(
                  'hidden md:block h-4 w-4 text-slate-500 transition-transform duration-200',
                  isUserMenuOpen && 'rotate-180 text-indigo-400'
                )}
              />
            </button>

            {/* Dropdown Menu */}
            {isUserMenuOpen && (
              <div
                className={cn(
                  'absolute right-0 top-full mt-2 w-[220px]',
                  'bg-[#1e293b] rounded-2xl',
                  'shadow-2xl shadow-black/40',
                  'border border-white/[0.08]',
                  'overflow-hidden z-[9999]',
                  'animate-scale-up'
                )}
              >
                {/* User Info Header */}
                <div className="px-4 py-4 border-b border-white/[0.08]">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
                      <span className="text-sm font-bold text-white">
                        {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[14px] font-semibold text-white truncate">
                        {user?.full_name || user?.username || 'User'}
                      </p>
                      <p className="text-[12px] text-slate-400 truncate">{user?.email}</p>
                    </div>
                  </div>
                </div>

                {/* Menu Items */}
                <div className="py-2">
                  <button
                    className={cn(
                      'flex w-full items-center gap-3 px-4 py-2.5',
                      'text-slate-300',
                      'hover:bg-white/[0.05] hover:text-white',
                      'transition-colors duration-150'
                    )}
                    onClick={() => {
                      setUserMenuOpen(false)
                      openProfile()
                    }}
                  >
                    <User className="h-[18px] w-[18px] text-slate-400" />
                    <span className="text-[14px] font-medium">Profile Settings</span>
                  </button>
                  <button
                    className={cn(
                      'flex w-full items-center gap-3 px-4 py-2.5',
                      'text-slate-300',
                      'hover:bg-white/[0.05] hover:text-white',
                      'transition-colors duration-150'
                    )}
                    onClick={() => {
                      setUserMenuOpen(false)
                      openSettings()
                    }}
                  >
                    <Settings className="h-[18px] w-[18px] text-slate-400" />
                    <span className="text-[14px] font-medium">Preferences</span>
                  </button>
                </div>

                {/* Logout */}
                <div className="border-t border-white/[0.08] py-2">
                  <button
                    onClick={handleLogout}
                    className={cn(
                      'flex w-full items-center gap-3 px-4 py-2.5',
                      'text-slate-300',
                      'hover:bg-white/[0.05]',
                      'transition-colors duration-150'
                    )}
                  >
                    <LogOut className="h-[18px] w-[18px] text-orange-500" />
                    <span className="text-[14px] font-medium">Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Settings Panel */}
      <SettingsPanel isOpen={isSettingsOpen} onClose={() => setSettingsOpen(false)} />

      {/* Profile Panel (Slide-over) */}
      <ProfilePanel isOpen={isProfileOpen} onClose={() => setProfileOpen(false)} />
    </header>
  )
}
